import * as uuid from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {
  COLLAPSED_NODE_INFO,
  END_NODE_INFO,
  OUTPUT_NODE_INFO,
  ROOT_NODE_INFO,
  ROUTER_NODE_INFO,
  TASK_NODE_INFO,
} from 'processes/graph-builder-processes/ProcessFlowchartNodeTypes';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';

import {InaccessibleInfoGraphQL} from '../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ProcessFlowchartGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessFlowchartGraphQL';
import {ProcessRouterNodeDetailsGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessRouterNodeDetailsGraphQL';
import {ProcessTaskNodeDetailsGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskNodeDetailsGraphQL';
import {
  OUTPUT_NODE_CLIENT_ID,
  ProcessFlowchartStaticNode,
  ProcessFlowchartUIOnlyNodeType,
  ROOT_NODE_CLIENT_ID,
} from '../ProcessFlowchartVizConstants';
import {isEndNodeDetailsGQL, isRouterNodeDetailsGQL, isTaskNodeDetailsGQL} from './getNodeDetailsTypeForGQL';

type ProcessFlowchartNodeGraphQL = ProcessFlowchartGraphQL['nodeConfigs'][number];

interface TaskSchemaForNode {
  __typename: string;
  id: string;
  name: string;
}

export function makeRootNode(name: string, clientId?: string): ProcessFlowchartStaticNode {
  return {
    clientId: clientId ?? ROOT_NODE_CLIENT_ID,
    name,
    instanceData: ROOT_NODE_INFO.instanceData,
    type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
  };
}

export function makeOutputNode(clientId?: string): ProcessFlowchartStaticNode {
  return {
    clientId: clientId ?? OUTPUT_NODE_CLIENT_ID,
    name: 'Outputs',
    instanceData: OUTPUT_NODE_INFO.instanceData,
    type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
  };
}

export function makeCollapsedNode(
  clientId?: string,
  numNodesRepresented: number = 0
): ProcessFlowchartStaticNode {
  return {
    clientId: clientId ?? uuid.v4(),
    instanceData: COLLAPSED_NODE_INFO.instanceData,
    name: `${numNodesRepresented}`,
    type: ProcessFlowchartUIOnlyNodeType.COLLAPSED_NODE,
    numNodesRepresented,
  };
}

export function makeIntermediateTaskNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  clientId?: string
): ProcessFlowchartStaticNode {
  assert(isTaskNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected task node to have task node details');
  const taskNodeDetails = nodeGQL.nodeDetails as ProcessTaskNodeDetailsGraphQL;
  const taskSchema = taskNodeDetails.schema as TaskSchemaForNode | InaccessibleInfoGraphQL;
  const noAccess = isInaccessible(taskSchema);
  return {
    clientId: clientId ?? uuid.v4(),
    ...(noAccess ? {isInaccessible: true} : null),
    name: noAccess ? NO_ACCESS_TEXT : nodeGQL.name,
    instanceData: TASK_NODE_INFO.instanceData,
    type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
  };
}

export function makeEndNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  clientId?: string
): ProcessFlowchartStaticNode {
  assert(isEndNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected end node to have end node details');
  return {
    clientId: clientId ?? uuid.v4(),
    name: nodeGQL.name,
    instanceData: END_NODE_INFO.instanceData,
    type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
  };
}

export function makeIntermediateRouterNode(
  nodeGQL: ProcessFlowchartNodeGraphQL,
  clientId?: string
): ProcessFlowchartStaticNode {
  assert(isRouterNodeDetailsGQL(nodeGQL.nodeDetails), 'Expected router node to have router node details');
  const routerNodeDetails = nodeGQL.nodeDetails as ProcessRouterNodeDetailsGraphQL;
  const nodeClientId = clientId ?? uuid.v4();

  const getOutboundEdgeId = (
    edge: {fromNodeConfig: {id: string}; toNodeConfig: {id: string}} | null
  ): string => {
    if (edge == null) {
      return getClientIdForProcessFlowchartEdge(nodeClientId, OUTPUT_NODE_CLIENT_ID);
    }
    return getClientIdForProcessFlowchartEdge(edge.fromNodeConfig.id, edge.toNodeConfig.id);
  };

  const outboundEdgeIdToLabel = routerNodeDetails.routerFunctions.reduce(
    (acc, curr) => {
      const edgeId = getOutboundEdgeId(curr.edgeConfig);
      acc[edgeId] = curr.name;
      return acc;
    },
    {} as Record<string, string>
  );

  return {
    clientId: nodeClientId,
    name: nodeGQL.name,
    instanceData: ROUTER_NODE_INFO.instanceData,
    type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
    outboundEdgeIdToLabel,
  };
}

export function makeTaskOrControlNode(nodeGQL: ProcessFlowchartNodeGraphQL): ProcessFlowchartStaticNode {
  if (isTaskNodeDetailsGQL(nodeGQL.nodeDetails)) {
    return makeIntermediateTaskNode(nodeGQL, nodeGQL.id);
  } else if (isRouterNodeDetailsGQL(nodeGQL.nodeDetails)) {
    return makeIntermediateRouterNode(nodeGQL, nodeGQL.id);
  } else if (isEndNodeDetailsGQL(nodeGQL.nodeDetails)) {
    return makeEndNode(nodeGQL, nodeGQL.id);
  }
  assert(false, 'Found unexpected node type.', {nodeGQL});
}
